// Analogous to Tfs/Service/WebAccess/Widgets/Scripts/Velocity/VelocityDataContract.ts

/// <amd-module name="Analytics/Boards/BoardsQueries/DataContracts" />
/** Provides a breakdown of work quantities. */
export interface Work {
    IterationSK: string;
    AggregationResult: number;
}

/**
 * Provides result of an aggregate quantity (sum or count), associated with a given iteration * State Category.
 * e.g. 15 story points in "Completed" state category, in Iteration 105.
 */
export interface Metastate extends Work {
    StateCategory: string;
    State: string;
}

/**
 *  Provides an way to pack variable typed, conditional on the "mode" which was active for configuration.
 *
 * Concrete example: A user could choose one work item type from a list, or they could choose one backlog level from a list.
 *   The "identifier" describes which mode (work item type vs backlog level) was active
 *   The "settings" describes which specific choice was selected *from the active mode*.
 */
export interface ModefulValueSetting<TMode, TValue> {
    /** Describes what mode was active for storing this setting. */
    identifier: TMode;
    /** Describes the settings value for the currently selected mode identifier. */
    settings: TValue;
}

/**
 * Describes the mode for performing an aggregation of data
 */
export enum AggregationMode {
    Count = 0,
    Sum = 1
}

export interface WorkItemTypeField {
    FieldName: string;
    FieldReferenceName: string;
    FieldType: string;
    WorkItemType: string;
}

/**
 * Provides particular work item ids for detail-viewing.
 */
export interface WorkItem {
    WorkItemId: string;
}

/**
 * Extends Work Item list result with state category the work item was in at query context.
 */
export interface WorkItemWithState extends WorkItem {
    StateCategory: string;
    State: string;
}

/** Describes a team, scoped to a particular project. */
export interface TeamScope {
    /** Guid of the project. This may get relaxed, if our client scenarios can optimally run without it. e.g. a *public REST API* which can describe teams from a variety of projects. */
    projectId: string;
    /** Guid of the team. */
    teamId: string;
}

export enum WitFieldType {
    Internal = 0,
    String = 1,
    Integer = 2,
    DateTime = 3,
    PlainText = 5,
    Html = 7,
    TreePath = 8,
    History = 9,
    Double = 10,
    Guid = 11,
    Boolean = 12,
    PicklistInteger = 14,
    PicklistString = 15
}

export namespace WiqlOperators {
    export const NotSpace = "NOT";
    export const OperatorAnd = "AND";
    export const OperatorOr = "OR";
    export const OperatorContains = "CONTAINS";
    export const OperatorNotContains = "NOT CONTAINS";
    export const OperatorContainsWords = "CONTAINS WORDS";
    export const OperatorNotContainsWords = "NOT CONTAINS WORDS";
    export const OperatorIn = "IN";
    export const OperatorNotIn = "NOT IN";
    export const OperatorEver = "EVER";
    export const OperatorNotEver = "NOT EVER";
    export const OperatorUnder = "UNDER";
    export const OperatorNotUnder = "NOT UNDER";
    export const OperatorInGroup = "IN GROUP";
    export const OperatorNotInGroup = "NOT IN GROUP";
    export const OperatorEqualTo = "=";
    export const OperatorNotEqualTo = "<>";
    export const OperatorIsEmpty = "IS EMPTY";
    export const OperatorIsNotEmpty = "IS NOT EMPTY";
    export const OperatorGreaterThan = ">";
    export const OperatorLessThan = "<";
    export const OperatorGreaterThanOrEqualTo = ">=";
    export const OperatorLessThanOrEqualTo = "<=";
    export const MacroStart = "@";
    export const MacroToday = "@today";
    export const MacroMe = "@me";
    export const MacroProject = "@project";
    export const MacroCurrentIteration = "@currentIteration";
    export const MacroTeamAreas = "@teamAreas";
    export const MacroFollows = "@follows";
    export const MacroRecentMentions = "@recentMentions";
    export const MacroMyRecentActivity = "@myRecentActivity";
    export const MacroRecentProjectActivity = "@recentProjectActivity";
    export const MacroStartOfYear = "@startOfYear";
    export const MacroStartOfMonth = "@startOfMonth";
    export const MacroStartOfWeek = "@startOfWeek";
    export const MacroStartOfDay = "@startOfDay";
}

export interface ODataWorkItemFieldDescriptor {
    /** Name of the field */
    FieldName: string;
    /** Type of the field */
    FieldType: string;
    /** Reference name of the field in VSTS  */
    FieldReferenceName: string;
}

/** Client-side type representating work item formatting for use by query generators
 * Note: Possible field types are at https://www.visualstudio.com/en-us/docs/work/reference/field-definition-element-reference#attributes-and-elements
 * The fields match analytics naming
 */
export interface WorkItemFieldDescriptor {
    /** Name of the field */
    FieldName: string;

    /** Type of the field */
    FieldType: string;

    /** Reference name of the field in VSTS  */
    FieldReferenceName: string;

    /** Should we use quotes to pass the value to Analytics, or pass value as-is */
    /** This value is calculated client-side. */
    UseQuotes: boolean;

    /** When making an analytics query, this is the path to use. */
    /** This value is calculated client-side. */
    AnalyticsFilterName: string;

    /** Function to figure out the value to query by from a FieldFilter */
    /** This function is calculated client-side. */
    GetAnalyticsQueryValueFunc(filter: FieldFilter): string;
}

/** Describes a filter for a field  */
export interface FieldFilter {
    /** Reference name of the field */
    fieldName: string;
    /** string representation of OData query operator. Consumer should validate against a whitelist.*/
    queryOperation: string;
    /** User-supplied query value. */
    queryValue: string;
}
